import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Spinner, Alert, Badge, Modal, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiCall from '../shared/apiCall';
import SlidingMenu from '../components/SlidingMenu';
import VersionFooter from '../components/VersionFooter';
import { canUser, refreshAccountData, getUserTier } from '../shared/accountCache';
import { TIER_PERMISSIONS } from '../shared/tierPermissions';
import { parseEmails } from '../shared/clientServerUtility';
import { formatDateWithLocale } from '../shared/utils';

export default function Groups() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [canManageGroups, setCanManageGroups] = useState(false);
  const [userTier, setUserTier] = useState('free');
  const [groupsLimit, setGroupsLimit] = useState(null);
  const [emailsPerGroupLimit, setEmailsPerGroupLimit] = useState(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formData, setFormData] = useState({ name: '', emails: '' });
  const [isSaving, setIsSaving] = useState(false);
  
  const navigate = useNavigate();

  // Check permission and fetch groups
  // Note: Authentication is handled by ProtectedRoute wrapper
  useEffect(() => {
    checkPermissionAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPermissionAndFetch = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const hasPermission = await canUser('canManageGroups');
      setCanManageGroups(hasPermission);
      
      if (!hasPermission) {
        setIsLoading(false);
        return;
      }
      
      await fetchGroups();
    } catch (err) {
      console.error('Error checking permission:', err);
      setError(t('groups.errorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await apiCall('/api/shared?action=GET_GROUPS', {}, 'POST');
      
      if (response.success) {
        setGroups(response.groups || []);
      } else {
        setError(response.message || 'Failed to load groups.');
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError('An error occurred while fetching groups.');
    }
  };

  const handleCreateGroup = () => {
    setSelectedGroup(null);
    setFormData({ name: '', emails: '' });
    setShowGroupModal(true);
  };

  const handleEditGroup = (group) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name || '',
      emails: group.emails.join(', ')
    });
    setShowGroupModal(true);
  };

  const handleDeleteGroup = (group) => {
    setSelectedGroup(group);
    setShowDeleteModal(true);
  };

  const handleSaveGroup = async () => {
    if (!formData.name || formData.name.trim() === '') {
      setError(t('groups.pleaseEnterGroupName'));
      return;
    }
    
    if (!formData.emails || formData.emails.trim() === '') {
      setError(t('groups.pleaseEnterEmail'));
      return;
    }
    
    // Validate emails
    const parsedEmails = parseEmails(formData.emails);
    if (parsedEmails.length === 0) {
      setError(t('groups.noValidEmails'));
      return;
    }
    
    // Check groups limit (only for creating new groups, not editing)
    if (!selectedGroup && groupsLimit && groups.length >= groupsLimit) {
      setError(t('groups.groupLimitReached', { limit: groupsLimit }));
      return;
    }
    
    setIsSaving(true);
    setError('');
    
    try {
      if (selectedGroup) {
        // Update existing group
        const response = await apiCall('/api/shared?action=UPDATE_GROUP', {
          groupId: selectedGroup._id,
          name: formData.name.trim(),
          emails: formData.emails
        }, 'POST');
        
        if (response.success) {
          setShowGroupModal(false);
          setSelectedGroup(null);
          setFormData({ name: '', emails: '' });
          // Refresh account cache (which includes groups)
          await refreshAccountData();
          await fetchGroups();
        } else {
          setError(response.message || t('groups.failedToUpdate'));
        }
      } else {
        // Create new group
        const response = await apiCall('/api/shared?action=CREATE_GROUP', {
          name: formData.name.trim(),
          emails: formData.emails
        }, 'POST');
        
        if (response.success) {
          setShowGroupModal(false);
          setSelectedGroup(null);
          setFormData({ name: '', emails: '' });
          // Refresh account cache (which includes groups)
          await refreshAccountData();
          await fetchGroups();
        } else {
          setError(response.message || t('groups.failedToCreate'));
        }
      }
    } catch (err) {
      console.error('Error saving group:', err);
      setError(t('groups.errorSaving'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedGroup) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const response = await apiCall('/api/shared?action=DELETE_GROUP', {
        groupId: selectedGroup._id
      }, 'POST');
      
      if (response.success) {
        setShowDeleteModal(false);
        setSelectedGroup(null);
        // Refresh account cache (which includes groups)
        await refreshAccountData();
        await fetchGroups();
      } else {
        setError(response.message || 'Failed to delete group.');
      }
    } catch (err) {
      console.error('Error deleting group:', err);
      setError('An error occurred while deleting the group.');
    } finally {
      setIsSaving(false);
    }
  };

  // Note: Authentication is handled by ProtectedRoute wrapper, so currentUser is guaranteed to exist

  return (
    <div className="groups-page">
      <SlidingMenu title={t('groups.title')} />

      <Container className="py-4">
        {error && (
          <Alert variant="danger" className="mb-4" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        <div className="d-flex justify-content-between align-items-center mb-3">
          {canManageGroups && (
            <>
              {groupsLimit && groups.length >= groupsLimit ? (
                <Alert variant="warning" className="mb-0">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {t('groups.groupLimitReached', { limit: groupsLimit })}
                </Alert>
              ) : (
                <Button variant="primary" onClick={handleCreateGroup}>
                  <i className="bi bi-plus-circle me-2"></i>
                  {t('groups.createGroup')}
                </Button>
              )}
            </>
          )}
        </div>

        {canManageGroups && (
          <Card className="mb-4 bg-light">
            <Card.Body>
              <p className="mb-0">
                {t('groups.description')}
              </p>
            </Card.Body>
          </Card>
        )}

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </Spinner>
            <p className="mt-2">{t('groups.loadingGroups')}</p>
          </div>
        ) : !canManageGroups ? (
          <Card className="text-center py-5">
            <Card.Body>
              <h3 className="mb-3">{t('planDetail.upgrade.proFeatureRequired')}</h3>
              <p className="text-muted mb-4">
                {t('planDetail.upgrade.managingGroups')}
              </p>
              <Button variant="primary" onClick={() => navigate('/pricing')}>
                <i className="bi bi-star me-2"></i>
                {t('planDetail.upgrade.upgradeToPro')}
              </Button>
            </Card.Body>
          </Card>
        ) : groups.length === 0 ? (
          <Card className="text-center py-5">
            <Card.Body>
              <h3 className="mb-3">{t('groups.noGroupsYet')}</h3>
              <p className="text-muted mb-4">
                {t('groups.noGroupsText')}
              </p>
              <Button variant="primary" onClick={handleCreateGroup}>
                <i className="bi bi-plus-circle me-2"></i>
                {t('groups.createGroup')}
              </Button>
            </Card.Body>
          </Card>
        ) : (
          <div className="row">
            {groups.map((group) => (
              <div key={group._id} className="col-md-6 col-lg-4 mb-4">
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <Card.Title>
                      {group.name || 'Unnamed Group'}
                    </Card.Title>
                    <Card.Text>
                      <Badge bg="secondary" className="me-2">
                        {group.emailCount || group.emails?.length || 0} email{group.emailCount !== 1 ? 's' : ''}
                      </Badge>
                      <small className="text-muted d-block mt-2">
                        Created: {formatDateWithLocale(group.createdAt, 'PPP')}
                      </small>
                    </Card.Text>
                  </Card.Body>
                  <Card.Footer className="bg-white">
                    <div className="d-flex justify-content-between">
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => handleEditGroup(group)}
                      >
                        <i className="bi bi-pencil me-1"></i>
                        Edit
                      </Button>
                      <Button 
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteGroup(group)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </Card.Footer>
                </Card>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Group Modal */}
        <Modal show={showGroupModal} onHide={() => {
          setShowGroupModal(false);
          setSelectedGroup(null);
          setFormData({ name: '', emails: '' });
        }} centered>
          <Modal.Header closeButton>
            <Modal.Title>{selectedGroup ? t('groups.editGroupModal') : t('groups.createGroupModal')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!selectedGroup && groupsLimit && groups.length >= groupsLimit && (
              <Alert variant="warning" className="mb-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {t('groups.groupLimitReached', { limit: groupsLimit })}
              </Alert>
            )}
            {emailsPerGroupLimit && emailsPerGroupLimit > 0 && parseEmails(formData.emails).length > emailsPerGroupLimit && (
              <Alert variant="warning" className="mb-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {t('groups.emailsPerGroupLimitReached', { limit: emailsPerGroupLimit })}
              </Alert>
            )}
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>{t('groups.groupName')}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t('groups.groupNamePlaceholder')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('groups.emailAddresses')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder={t('groups.emailAddressesPlaceholder')}
                  value={formData.emails}
                  onChange={(e) => setFormData({ ...formData, emails: e.target.value })}
                  dir="ltr"
                />
                <Form.Text className="text-muted">
                  {t('groups.emailAddressesHelp')}
                </Form.Text>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowGroupModal(false);
              setSelectedGroup(null);
              setFormData({ name: '', emails: '' });
            }}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={handleSaveGroup} disabled={isSaving || (emailsPerGroupLimit && emailsPerGroupLimit > 0 && parseEmails(formData.emails).length > emailsPerGroupLimit)}>
              {isSaving ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {selectedGroup ? t('groups.saving') : t('groups.creating')}
                </>
              ) : (
                selectedGroup ? t('groups.saveChanges') : t('groups.createGroup')
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('groups.deleteGroupModal')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t('groups.deleteConfirmation', { name: selectedGroup?.name || t('groups.unnamedGroup') })}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              variant="danger" 
              onClick={handleConfirmDelete}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {t('groups.deleting')}
                </>
              ) : (
                t('common.delete')
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
      
      <VersionFooter />
    </div>
  );
}
